'use client';

import { useEffect, useRef, useState } from 'react';

import styles from './BaseDropdown.module.scss';

export interface DropdownItem<T = string | number> {
    label: string;
    value: T;
}

interface BaseDropdownProps<T = string | number> {
    value: T | null;
    options: DropdownItem<T>[];
    placeholder?: string;
    onChange: (value: T) => void;
    isDisabled?: boolean;
    label?: string;
    directionY?: 'top' | 'bottom';
    uiType?: string;
    className?: string;
}

export const BaseDropdown = <T extends string | number = string>({
    value,
    options,
    placeholder = 'Select',
    onChange,
    isDisabled = false,
    label = 'dropdown',
    directionY = 'bottom',
    uiType,
    className = '',
}: BaseDropdownProps<T>) => {
    const [isOptionsShown, setIsOptionsShown] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(0);
    const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const selectedItem = options.find((option) => option.value === value) || {
        label: '',
        value: null,
    };

    const selectedLabel = selectedItem.label || placeholder;
    const selectedIndex = options.findIndex((option) => option.value === value) || 0;

    const openDropdown = () => {
        if (isDisabled) return;
        setIsOptionsShown(true);
        setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
        setTimeout(() => {
            optionRefs.current[selectedIndex >= 0 ? selectedIndex : 0]?.focus();
        }, 0);
    };

    const closeDropdown = () => {
        setIsOptionsShown(false);
    };

    const selectOption = (option: DropdownItem<T>) => {
        onChange(option.value);
        setIsOptionsShown(false);
    };

    const handleKeydown = (event: React.KeyboardEvent, index: number) => {
        const { key } = event;

        switch (key) {
            case 'ArrowDown':
                event.preventDefault();
                if (focusedIndex < options.length - 1) {
                    const newIndex = focusedIndex + 1;
                    setFocusedIndex(newIndex);
                    optionRefs.current[newIndex]?.focus();
                }
                break;
            case 'ArrowUp':
                event.preventDefault();
                if (focusedIndex > 0) {
                    const newIndex = focusedIndex - 1;
                    setFocusedIndex(newIndex);
                    optionRefs.current[newIndex]?.focus();
                }
                break;
            case 'Tab':
                setFocusedIndex(index);
                // Close dropdown when tabbing out of the last/first item
                if (
                    (!event.shiftKey && index >= options.length - 1) ||
                    (event.shiftKey && index <= 0)
                ) {
                    setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
                    closeDropdown();
                }
                break;
            case 'Enter':
                event.preventDefault();
                selectOption(options[focusedIndex]);
                break;
            case 'Escape':
                event.preventDefault();
                closeDropdown();
                break;
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                closeDropdown();
            }
        };

        if (isOptionsShown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOptionsShown]);

    return (
        <div
            ref={dropdownRef}
            className={`${styles.dropdown} ${className} ${
                uiType ? styles[`dropdown--${uiType}`] : ''
            } ${isOptionsShown ? styles['is-options-shown'] : ''} ${
                isDisabled ? styles['is-disabled'] : ''
            } ${!selectedItem.value ? styles['is-placeholder'] : ''} ${
                directionY === 'top' ? styles['is-direction-top'] : ''
            } ${selectedItem.value ? styles['is-selected'] : ''}`}
        >
            {isOptionsShown && <span className={styles.dropdown__bg} onClick={closeDropdown} />}
            <span className={styles.dropdown__wrap}>
                <span>
                    <button
                        type="button"
                        className={`${styles.dropdown__selectedItem} ${
                            selectedItem.value ? styles['is-selected'] : ''
                        }`}
                        aria-label={label}
                        title={selectedLabel}
                        onClick={openDropdown}
                        disabled={isDisabled}
                    >
                        <span className={styles.dropdown__selectedText}>{selectedLabel}</span>
                        <svg
                            className={styles.dropdown__iconArrowDown}
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M4 6L8 10L12 6"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>
                </span>
                {isOptionsShown && (
                    <ul className={styles.dropdown__optionList}>
                        {options.map((option, index) => (
                            <li
                                key={`${option.value}-${option.label}`}
                                className={`${styles.dropdown__optionItems} ${
                                    selectedItem.value === option.value ? styles['is-selected'] : ''
                                } ${focusedIndex === index ? styles['is-focused'] : ''}`}
                            >
                                <button
                                    ref={(el) => {
                                        optionRefs.current[index] = el;
                                    }}
                                    className={styles.dropdown__optionLabel}
                                    onClick={() => selectOption(option)}
                                    onKeyDown={(e) => handleKeydown(e, index)}
                                    tabIndex={0}
                                >
                                    <span>{option.label}</span>
                                    {selectedItem.value === option.value && (
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M13 4L6 11L3 8"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    )}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </span>
        </div>
    );
};
